// =============================================================================
// app/src/lib/pedidoWhatsApp.ts — o pedido em texto, para colar no grupo.
//
// Pedido do dono (18/08/2026): "copiar pedido para WhatsApp, para colar
// manualmente no grupo do representante — nome da peça, código, quantidade".
//
// Por que texto puro e não link: o pedido vai para um GRUPO, e o link de
// WhatsApp abre conversa com UMA pessoa. Copiar e colar é o que o gerente já
// faz hoje à mão — o sistema só entrega o texto pronto e sem erro de
// digitação.
//
// Função pura: o que vem do banco (carrinho ou pedido já emitido) tem o mesmo
// formato, então o mesmo texto serve aos dois lugares.
// =============================================================================

export interface LinhaDoPedido {
  descricao: string;
  /** Código do fornecedor > do fabricante > SKU. Nulo na peça avulsa. */
  codigo: string | null;
  quantidade: number;
}

/** Quantidade sem casas quando é inteira: "2 un", não "2,000 un". */
function quantidadeCurta(n: number): string {
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : String(v).replace('.', ',');
}

export function textoDoPedidoWhatsApp(
  linhas: LinhaDoPedido[],
  cabecalho: { fornecedor?: string | null; data?: string | null; loja?: string | null } = {},
): string {
  const partes: string[] = [];

  // O cabeçalho existe para o representante saber de quem é o pedido sem
  // perguntar — no grupo dele chegam pedidos de várias lojas.
  const titulo = ['*Pedido*', cabecalho.loja ? `— ${cabecalho.loja}` : null]
    .filter(Boolean).join(' ');
  partes.push(titulo);
  if (cabecalho.fornecedor) partes.push(cabecalho.fornecedor);
  if (cabecalho.data) partes.push(cabecalho.data);
  partes.push('');

  for (const l of linhas) {
    // "nome - código - qtd", exatamente o formato pedido. Sem código, a linha
    // não fica com um traço solto no meio.
    partes.push([
      l.descricao.trim(),
      l.codigo?.trim() || null,
      `${quantidadeCurta(l.quantidade)} un`,
    ].filter(Boolean).join(' - '));
  }

  return partes.join('\n');
}

/**
 * Copia para a área de transferência. Devolve false quando o navegador não
 * deixa (contexto inseguro, permissão negada) — aí a tela mostra o texto para
 * o gerente selecionar à mão, em vez de fingir que copiou.
 */
export async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}
